import { Close, CopyAll, CopyAllTwoTone, KeyboardArrowRight, PictureAsPdf, RestartAlt } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from '../../reducers/hooks';
import { Api } from '../../App';
import './CalculateStep.css';
import { Page, Text, View, Document, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';
import { reset as materialReset } from "../../reducers/materialReducer";
import { finetuneFeed, finetuneSpeed, reset as toolReset, ToolState } from "../../reducers/toolReducer";
import { reset as machineReset } from "../../reducers/machineReducer";
import { getTrackBackground, Range as ReactRange } from "react-range";

function CalculateStep({dispatchStep}:any) {
    const [material, machine, tool] = useAppSelector((state) => [state.material, state.machine, state.tool]);
    const dispatch = useAppDispatch();
    const [isCopyAnim, setIsCopyAnim] = useState(false);
    const [finetuneOpen, setFinetuneOpen] = useState(sessionStorage.getItem('advisor-finetuneOpen') === 'true');
    const [results, setResults] = useState<null|any>(null);

    const toggleFinetuneOpen = () => {
        setFinetuneOpen(!finetuneOpen);
        sessionStorage.setItem('advisor-finetuneOpen', !finetuneOpen === true ? 'true' : 'false');
    }

    useEffect(() => {
        if (material && tool && results === null) {
            // In the case of fatal errors, change to array so that error is shown
            Api.calculate(material, machine, tool).then((res) => setResults(typeof res === 'string' ? [] : res));
        }
    })

    const generateResults = (results: any, tool: ToolState, feedFinetuning: number, speedFinetuning: number) => {

        let result = {
            'Speed (RPM)':
                Math.round(results.speed * speedFinetuning),
            [`Feed (${Api.displayPreferredUnits('feed')})`]:
                parseFloat(    
                    (results.feed * speedFinetuning * feedFinetuning)
                    .toFixed(2)
                ),
            [`Surface Speed (${Api.displayPreferredUnits('surfacespeed')})`]:
                Math.round(results.surfaceSpeed * speedFinetuning),
            [results.feedPerTooth ? `FPT: Programmed (${Api.displayPreferredUnits('feedpertooth')})` : `Feed per Revolution (${Api.displayPreferredUnits('feedperrevolution')})`]:
                parseFloat(
                    ((results.feedPerTooth || results.feedPerRevolution) * feedFinetuning)
                    .toFixed(5)
                ),
            [`Chip Thickness: Actual (${Api.displayPreferredUnits('length')})`]:
            parseFloat(
                ((results.chipThickness) * feedFinetuning)
                .toFixed(4)
            ),
            [`Material Removal Rate (${Api.displayPreferredUnits('removalrate')})`]:
                parseFloat(
                    (results.materialRemovalRate * feedFinetuning * speedFinetuning)
                        .toFixed(4)
                ),
        }
        if (!results.chipThickness)
            delete result[`Chip Thickness: Actual (${Api.displayPreferredUnits('length')})`];
        return result;
    }

    let resultText: object = {};
    
    // Set variables here to get around state update errors when updating invalid range values
    // e.g. if new range bounds are 0.8 to 1.2, and previous value is 1.5, reset to 1.0
    // Otherwise, the value will remain 1.5 for this state update, which is invalid
    let feedFinetuning = tool.feedFinetuning;
    let speedFinetuning = tool.speedFinetuning;
    if (results && !(results instanceof Array)) {
        if (tool.speedFinetuning < results.minRPMRangePct / 100 || tool.speedFinetuning > results.maxRPMRangePct / 100) {
            setTimeout(() => dispatch(finetuneSpeed(1)), 0);
            speedFinetuning = 1;
        } else if (tool.feedFinetuning < results.minIPMRangePct / 100 || tool.feedFinetuning > results.maxIPMRangePct / 100) {
            setTimeout(() => dispatch(finetuneFeed(1)), 0);
            feedFinetuning = 1;
        }
        resultText = generateResults(results, tool, feedFinetuning, speedFinetuning);
    }

    let inputs = Api.getPrintAttributes(material, machine, tool);

    const handleCopy = () => {
        if (results && !(results instanceof Array)) {

            let materialText = '';
            for (const key in inputs.material) {
                // @ts-ignore
                if (inputs.material[key] !== false) materialText += `- ${key}: ${inputs.material[key]}\n`;
            }

            let machineText = '';
            for (const key in inputs.machine) {
                // @ts-ignore
                if (inputs.machine[key] !== false) machineText += `- ${key}: ${inputs.machine[key]}\n`;
            }

            let toolText = '';
            for (const key in inputs.tool) {
                // @ts-ignore
                if (inputs.tool[key] !== false) toolText += `- ${key}: ${inputs.tool[key]}\n`;
            }


            navigator.clipboard.writeText([
                `--- Results ---`,
                ...Object.keys(resultText).map((key) => `${key}: ${(resultText as any)[key]}`),
                `--- Parameters ---`,
                `Material`,
                materialText,
                `Machine Parameters`,
                machineText,
                `${tool.type === 'endmill' ? 'Endmill' : 'Drill'}`,
                toolText,
                `--- Generated by GARR Technical Advisor on ${new Date().toLocaleString()} ---`
            ].join('\n'));
            setIsCopyAnim(true);
        }
    }

    const styles = StyleSheet.create({
        page: {
            flexDirection: 'column',
            gap: 0,
            margin: 0,
            fontSize: 12,
            backgroundColor: '#E4E4E4'
        },
        resultsSection: {
            padding: 10,
            margin: 0,
            flexDirection: 'row'
        },
        section: {
            padding: 10,
            margin: 0,
            flexDirection: 'column'
        },
        listItem: {
            padding: 10,
            textAlign: 'right',
            border: '1px solid black'
        },
        inputItem: {
            padding: 10,
            border: '1px solid black'
        },
        h2: {
            fontSize: 16,
            marginTop: 10
        },
        h1: {
            fontSize: 24,
            marginTop: 20,
            marginLeft: 20
        }
    });

        
    const MyDocument = () => {

        let materialText = '';
        for (const key in inputs.material) {
            // @ts-ignore
            if (inputs.material[key] !== false) materialText += `${key}: ${inputs.material[key]}\n`;
        }

        let machineText = '';
        for (const key in inputs.machine) {
            // @ts-ignore
            if (inputs.machine[key] !== false) machineText += `${key}: ${inputs.machine[key]}\n`;
        }

        let toolText = '';
        for (const key in inputs.tool) {
            // @ts-ignore
            if (inputs.tool[key] !== false) toolText += `${key}: ${inputs.tool[key]}\n`;
        }


        return <Document>
            <Page size="A4" style={styles.page}>
                <Text style={styles.h1}>GARR TOOL Technical Advisor (www.garrtool.com)</Text>
                <View style={styles.section}>
                    <Text style={styles.h2}>Material</Text>
                    <View style={styles.inputItem}>
                        <Text>{materialText}</Text>
                    </View>
                    <Text style={styles.h2}>Machine</Text>
                    <View style={styles.inputItem}>
                        <Text>{machineText}</Text>
                    </View>
                    <Text style={styles.h2}>Tool</Text>
                    <View style={styles.inputItem}>
                        <Text>{toolText}</Text>
                    </View>
                </View>
                <Text style={styles.h1}>Results</Text>
                <View style={styles.resultsSection}>
                    <View style={styles.section}>
                        {Object.keys(resultText).map((text, index) => <View style={styles.listItem} key={index}><Text>{text}</Text></View>)}
                    </View>
                    <View style={styles.section}>
                        {Object.values(resultText).map((text, index) => <View style={styles.listItem} key={index}><Text>{text}</Text></View>)}
                    </View>
                </View>
            </Page>
        </Document>
    };

    const handleRestart = () => {
        if (window.confirm('Are you sure you want to clear your selections? You can\'t undo this.')) {
            sessionStorage.clear();
            dispatch(machineReset());
            dispatch(toolReset());
            dispatch(materialReset());
            dispatchStep('restart');
        }
    }

    return (
        <div className="modal" onMouseDown={() => dispatchStep('-')}>
            <div className="modalContent" onMouseDown={(e) => e.stopPropagation()}>
                <h2>Results</h2>
                {results ?
                    results instanceof Array ?
                        (<div className="error">
                            Your current choices are invalid. As such, the Advisor is not able to provide recommendations. Please check your settings and try again.
                        </div>) :
                        (<>
                            <table className="results">
                                <tbody>
                                    {Object.keys(resultText).map((key, index) => (
                                        <tr className="result" key={index}>
                                            <th>{key}</th>
                                            <td>{(resultText as any)[key]}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="drawer">
                                <h2 className={`drawer-title ${finetuneOpen ? 'open' : ''}`} onClick={toggleFinetuneOpen}>
                                    <KeyboardArrowRight />
                                    Finetune Results
                                </h2>
                                <div className={`drawer-content ${finetuneOpen ? 'open' : ''}`}>
                                    <div className="aggressionSlider">
                                        <p>Conservative</p>
                                        <h3>Speed ({Math.round(speedFinetuning * 100)}%)</h3>
                                        <p>Aggressive</p>
                                    </div>
                                    <ReactRange
                                        min={results.minRPMRangePct / 100}
                                        max={results.maxRPMRangePct / 100}
                                        step={0.01}
                                        values={[speedFinetuning]}
                                        onChange={(values:number[]) => dispatch(finetuneSpeed(values[0]))}
                                        renderTrack={({ props, children }) => (
                                            <div
                                                onMouseDown={props.onMouseDown}
                                                onTouchStart={props.onTouchStart}
                                                className="rangeSlider"
                                                style={{
                                                    ...props.style,
                                                    height: "36px",
                                                    display: "flex",
                                                    width: "100%"
                                                }}
                                            >
                                                <div
                                                    ref={props.ref}
                                                    style={{
                                                        height: "5px",
                                                        width: "100%",
                                                        borderRadius: "4px",
                                                        background: getTrackBackground({
                                                            values: [speedFinetuning],
                                                            colors: ["var(--primary)", "#959595"],
                                                            min: results.minRPMRangePct / 100,
                                                            max: results.maxRPMRangePct / 100
                                                        }),
                                                        alignSelf: "center"
                                                    }}
                                                >
                                                    {children}
                                                </div>
                                            </div>
                                        )}
                                        renderThumb={({ props }) => (
                                            <div
                                                {...props}
                                                style={{
                                                ...props.style,
                                                height: '20px',
                                                width: '10px',
                                                backgroundColor: 'var(--primary)'
                                                }}
                                            />
                                        )}
                                    />
                                    <div className="aggressionSlider">
                                        <p>Conservative</p>
                                        <h3>Feed ({Math.round(feedFinetuning * 100)}%)</h3>
                                        <p>Aggressive</p>
                                    </div>
                                    <ReactRange
                                        min={results.minIPMRangePct / 100}
                                        max={results.maxIPMRangePct / 100}
                                        step={0.01}
                                        values={[feedFinetuning]}
                                        onChange={(values:number[]) => dispatch(finetuneFeed(values[0]))}
                                        renderTrack={({ props, children }) => (
                                            <div
                                                onMouseDown={props.onMouseDown}
                                                onTouchStart={props.onTouchStart}
                                                className="rangeSlider"
                                                style={{
                                                    ...props.style,
                                                    height: "36px",
                                                    display: "flex",
                                                    width: "100%"
                                                }}
                                            >
                                                <div
                                                    ref={props.ref}
                                                    style={{
                                                        height: "5px",
                                                        width: "100%",
                                                        borderRadius: "4px",
                                                        background: getTrackBackground({
                                                            values: [feedFinetuning],
                                                            colors: ["var(--primary)", "#959595"],
                                                            min: results.minIPMRangePct / 100,
                                                            max: results.maxIPMRangePct / 100
                                                        }),
                                                        alignSelf: "center"
                                                    }}
                                                >
                                                    {children}
                                                </div>
                                            </div>
                                        )}
                                        renderThumb={({ props }) => (
                                            <div
                                                {...props}
                                                style={{
                                                ...props.style,
                                                height: '20px',
                                                width: '10px',
                                                backgroundColor: 'var(--primary)'
                                                }}
                                            />
                                        )}
                                    />
                                    <div
                                        style={{marginBottom: '10px'}}
                                        className={(feedFinetuning !== 1 || speedFinetuning !== 1) ? "btn dangerBtn" : "btn disabledBtn"}
                                        onClick={() => { dispatch(finetuneFeed(1)); dispatch(finetuneSpeed(1))}}>
                                        Reset finetuning to default
                                    </div>
                                </div>
                            </div>
                        </>)
                : <div className="loading" />}
                <hr />
                <div className="btnrow">
                    <div className="btn primaryBtn" onClick={() => dispatchStep('-')}>
                        <Close />
                    </div>
                    <div className="btn dangerBtn" onClick={handleRestart}>
                        <RestartAlt />
                        <span>Start Over</span>
                    </div>
                    {(results && !(results instanceof Array)) &&
                        <>
                            <div
                                className={isCopyAnim ? "btn copied" : "btn"}
                                onClick={handleCopy}
                                onAnimationEnd={() => setIsCopyAnim(false)}
                            >
                                {isCopyAnim ? <CopyAllTwoTone /> : <CopyAll />}
                                <span>{isCopyAnim ? 'Copied!' : 'Copy'}</span>
                            </div>
                            <PDFDownloadLink
                                fileName="results.pdf"
                                document={<MyDocument />}
                                className="btn"
                            >
                                <PictureAsPdf />
                                <span>Export to PDF</span>
                            </PDFDownloadLink>
                        </>
                    }
                </div>
            </div>
        </div>
    )
}

export default CalculateStep;